import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useSubscription } from './hooks/useSubscription';
import Dashboard from './pages/Dashboard';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import Pricing from './pages/Pricing';
import PaymentSuccess from './pages/PaymentSuccess';
import AccountSettings from './pages/AccountSettings';
import Home from './pages/Home';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Reports from './pages/Reports';
import PurchaseHistory from './pages/PurchaseHistory';
import Resources from './pages/Resources';
import ATSHacks from './pages/resources/ATSHacks';
import ResumeRuleOfThirds from './pages/resources/ResumeRuleOfThirds';
import ResumeAdvantageChecklist from './pages/resources/ResumeAdvantageChecklist';
import AfterTheInterview from './pages/resources/AfterTheInterview';
import ClosingTheInterviewGuide from './pages/resources/ClosingTheInterviewGuide';
import LinkedInOptimizationChecklist from './pages/resources/LinkedInOptimizationChecklist';
import NailingTheCuriosityDrivenResume from './pages/resources/NailingTheCuriosityDrivenResume';
import NegotiationDosAndDonts from './pages/resources/NegotiationDosAndDonts';
import QuantifiableResultsWorksheet from './pages/resources/QuantifiableResultsWorksheet';
import ReadmeFIRST from './pages/resources/ReadmeFIRST';
import ResumeKeywordToolkit from './pages/resources/ResumeKeywordToolkit';
import TopTenResumeMistakesChecklist from './pages/resources/TopTenResumeMistakesChecklist';
import LinkedInProfileHacks from './pages/resources/LinkedInProfileHacks';
import TrickAndTrapQuestions from './pages/resources/TrickAndTrapQuestions';
import UltimateAnswerFormula from './pages/resources/UltimateAnswerFormula';
import InterviewerStyleGuide from './pages/resources/InterviewManagerStyleGuide';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import NicheJobBoards from './pages/resources/NicheJobBoards';
import AboutUs from './pages/AboutUs';
import SetPassword  from './pages/SetPassword';
import { AdminInviteUser } from './pages/Admin/Invite';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const { hasActiveSubscription, loading: subscriptionLoading } = useSubscription();
  
  if (authLoading || subscriptionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  if (!hasActiveSubscription) {
    return <Navigate to="/pricing" replace />;
  }

  return <>{children}</>;
}

const resourceRoutes = [
  { path: "/ats_hacks", component: ATSHacks },
  { path: "/resume_rule_of_thirds", component: ResumeRuleOfThirds },
  { path: "/2025_resume_advantage_checklist", component: ResumeAdvantageChecklist },
  { path: "/after_the_interview", component: AfterTheInterview },
  { path: "/closing_the_interview_guide", component: ClosingTheInterviewGuide },
  { path: "/linkedin_optimization_checklist", component: LinkedInOptimizationChecklist },
  { path: "/linkedin_profile_hacks", component: LinkedInProfileHacks },
  { path: "/trick_and_trap_questions", component: TrickAndTrapQuestions },
  { path: "/interviewer_style_guide", component: InterviewerStyleGuide },
  { path: "/nailing_the_curiosity_driven_resume", component: NailingTheCuriosityDrivenResume },
  { path: "/negotiation_dos_and_donts", component: NegotiationDosAndDonts },
  { path: "/quantifiable_results_worksheet", component: QuantifiableResultsWorksheet },
  { path: "/readme_first", component: ReadmeFIRST },
  { path: "/niche_job_boards", component: NicheJobBoards },
  { path: "/ultimate_answer_formula", component: UltimateAnswerFormula },
  { path: "/resume_keyword_toolkit", component: ResumeKeywordToolkit },
  { path: "/top_ten_resume_mistakes_checklist", component: TopTenResumeMistakesChecklist },
];

export default function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/admin/invite" element={<AdminInviteUser />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route
            path="/dashboard/*"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/account/settings"
            element={
              <PrivateRoute>
                <AccountSettings />
              </PrivateRoute>
            }
          />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/dashboard/reports" element={<Reports />} />
          <Route path="/dashboard/purchases" element={<PurchaseHistory />} />
          <Route
            path="/dashboard/resources/*"
            element={
              <PrivateRoute>
                <Resources />
              </PrivateRoute>
            }
          />
          {resourceRoutes.map(({ path, component }) => (
            <Route key={path} path={`/dashboard/resources${path}`} element={<PrivateRoute>{React.createElement(component)}</PrivateRoute>} />
          ))}
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}